<template>
  <div class="scene-wrapper">
    <!-- Debug Panel Component -->
    <!-- <div class="debug-toggle" @click="showDebug = !showDebug">Debug</div>
    <div v-if="showDebug" class="debug-panel">
      <h3>Debug Info</h3>
      <p>View Mode: {{ viewMode }}</p>
      <p>User ID: {{ userId }}</p>
      <p>Avatars Count: {{ userAvatars ? userAvatars.length : 0 }}</p>
      <button @click="refreshAvatars">Refresh Avatars</button>
      <button @click="setViewMode('list')">List View</button>
    </div> -->
    
    <!-- Show viewer wrapper only if mainProduct is loaded -->
    <div class="viewer-wrapper" v-if="mainProduct">
      <!-- SceneViewer: display the 3D scene if 'viewMode' is 'scene' -->
      <SceneViewer
        v-if="viewMode === 'scene'"
        :productlist="products"
        :product="mainProduct"
        :body="userAvatarUrl"
        :body-type="!userBodyId ? 'body_1' : userBodyId"
        :avatar-gender="avatarGender"
        :create-avatar="handleSetViewMode" 
        :slug="slug"
      />
      
      <!-- AvaturnViewer: for editing in 'edit' mode -->
      <AvaturnViewer
        v-if="viewMode === 'edit' && avaturnUrl"
        :reset="resetConnection"
        :avatar-loaded="handleAvatarLoaded"
        :go-back="handleSetViewMode"
        :url="avaturnUrl"
      />
      
      <!-- AvatarList: choose from existing avatars in 'list' mode -->
      <AvatarList
        v-if="viewMode === 'list'"
        :avatars="userAvatars" 
        :back="handleSetViewMode"
        :base-view="viewWithBaseModel"
        :avatar-selected="avatarSelected"
        :delete-avatar="handleDeleteAvatar"
        @image-error="handleAvatarImageError"
        @image-load="handleAvatarImageLoad"
      />
      
      <!-- CreateAvatar: the UI for capturing new avatar images in 'create' mode -->
      <CreateAvatar 
        v-if="viewMode === 'create'"
        :go-back="handleSetViewMode"
      />
      
      <!-- CreateAvatarForm: further configuration in 'init' mode -->
      <CreateAvatarForm
        v-if="viewMode === 'init'"
        :completed="avatarCreated"
        :go-back="handleSetViewMode"
        :uid="userId"
      />
      
      <!-- Loading indicator if isLoading is true -->
      <div v-if="isLoading" class="spinner-wrapper2">
        <div class="spinner2"></div>
        <MatchyLogo position="center" size="large" />
      </div>
    </div>
  </div>
</template>

<script>
import { onMounted, watch, ref } from 'vue';

import CreateAvatarForm from './components/CreateAvatarForm.vue';
import CreateAvatar     from './components/CreateAvatar.vue';
import AvaturnViewer   from './components/AvaturnViewer.vue';
import AvatarList      from './components/AvatarList.vue';
import MatchyLogo      from './components/MatchyLogo.vue';
import SceneViewer     from './components/SceneViewer.vue';

// Composables
import { useUserStore } from './composables/useStore';
import { useViewMode }  from './composables/useViewMode';
import { useProducts }  from './composables/useProducts';
import { useLogger }    from './composables/useLogger';
import { useAvatar }    from './composables/useAvatar';
import { API }          from '@/constants/urls';

export default {
  name: 'App',
  components: {
    SceneViewer,
    CreateAvatarForm,
    CreateAvatar,
    AvaturnViewer,
    AvatarList,
    MatchyLogo
  },
  setup() {
    // Debug state
    const showDebug = ref(false);
    
    // 1) Vuex-based user store
    const userStore = useUserStore();
    
    // 2) View mode composable
    const { viewMode, setViewMode } = useViewMode();
    
    // 3) Product data
    const { slug, mainProduct, products, initSlug, fetchProductData } = useProducts(API.BASE);
    
    // 4) Logger data
    const { logger, getLogger, updateLogger, createLogger } = useLogger(API.BASE, API.TOKEN);
    
    // 5) Avatar management
    const {
      isLoading,
      userAvatars,
      getUserAvatars,
      processAvatars,
      createNewSession,
      createNewUser,
      deleteAvatar,
      renderAvatarImage,
      handleAvatarLoaded: avatarLoadedHandler
    } = useAvatar(API.AVATURN, API.TOKEN, setViewMode);

    // Initialize slug from URL
    initSlug();
    
    // Watch for changes in viewMode
    watch(viewMode, async () => {
      console.log('View mode changed:', viewMode.value);
      // If user goes to "list" mode, re-fetch user avatars from Avaturn
      if (viewMode.value === 'list') {
        try {
          console.log('Fetching avatars for list view...');
          if (userStore.userId.value) {
            await getUserAvatars(userStore.userId.value);
            console.log('Avatars fetched, count:', userAvatars.value.length);
            
            // Verify avatar render URLs
            userAvatars.value.forEach((avatar, index) => {
              console.log(`Avatar ${index} (${avatar.id}): render_url = ${avatar.render_url || 'none'}`);
            });
          } else {
            console.warn('Cannot fetch avatars: No user ID');
          }
        } catch (error) {
          console.error('Error fetching avatars:', error);
        }
      }
    });
    
    /**
     * Refresh avatars manually for debugging
     */
    const refreshAvatars = async () => {
      console.log('Manually refreshing avatars...');
      if (userStore.userId.value) {
        await getUserAvatars(userStore.userId.value);
        console.log('Refreshed avatars:', userAvatars.value);
      } else {
        console.warn('Cannot refresh: No user ID');
      }
    };
    
    /**
     * Force a re-render of the avatar list
     */
    const forceRerender = () => {
      if (userAvatars.value && userAvatars.value.length) {
        console.log('Forcing re-render of avatar list...');
        userAvatars.value = [...userAvatars.value];
      }
    };
    
    /**
     * Handle image load errors
     */
    const handleImageError = async (avatarIndex) => {
      console.log(`Image load error for avatar at index: ${avatarIndex}`);
      
      // Try to fix the render URL
      if (userAvatars.value && userAvatars.value[avatarIndex]) {
        const avatar = userAvatars.value[avatarIndex];
        console.log(`Requesting new render for avatar ${avatar.id}...`);
        
        try {
          // Request a new render
          await renderAvatarImage(avatar.id);
          
          // Assign a temporary placeholder while render processes
          userAvatars.value[avatarIndex] = {
            ...avatar,
            render_url: 'data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAAAEAAAABCAQAAAC1HAwCAAAAC0lEQVR42mNkYAAAAAYAAjCB0C8AAAAASUVORK5CYII='
          };
          
          // Refresh avatars after a delay to see if render is complete
          setTimeout(refreshAvatars, 5000);
        } catch (error) {
          console.error('Error handling image error:', error);
        }
      }
    };
    
    /**
     * Switch view mode with optional logic
     * @param {string} mode 
     */
    const handleSetViewMode = (mode) => {
      console.log(`Setting view mode to: ${mode}`);
      setViewMode(mode, userAvatars.value);
    };
    
    /**
     * Show the plain base mannequin (no custom user avatar)
     */
    const viewWithBaseModel = () => {
      userStore.userAvatarUrl.value = null;
      userStore.userBodyId.value    = 'body_1';
      userStore.avatarGender.value  = null;
      setViewMode('scene');
    };
    
    /**
     * When user selects an existing avatar for editing
     * @param {Object} avatar 
     */
    const avatarSelected = async (avatar) => {
      // Verify avatar data
      if (!avatar || !avatar.id) {
        console.error('Invalid avatar selected:', avatar);
        return;
      }
      
      console.log('Avatar selected for editing:', avatar);
      try {
        await createNewSession(userStore.userId.value, avatar.id, userStore);
      } catch (error) {
        console.error('Error creating editing session:', error);
      }
    };
    
    /**
     * If we have no user ID, create a new user. Otherwise, fetch existing avatars.
     */
    const resetConnection = async () => {
      try {
        if (!userStore.userId.value) {
          console.log('No user ID, creating new user...');
          // create new user
          const newUser = await createNewUser();
          if (newUser) {
            console.log('New user created:', newUser);
            userStore.userId.value = newUser.id;
            await createLogger(newUser.id);
          }
        } else {
          console.log('Existing user, refreshing avatars...');
          // re-fetch existing avatars
          await getUserAvatars(userStore.userId.value);
          
          console.log('Session info:', userStore.sessionId.value, userStore.userAvatarUrl.value,
                      userStore.avatarGender.value, userStore.userBodyId.value);
          // Switch to avatar list
          setViewMode('list');
        }
      } catch (error) {
        console.error('Error resetting connection:', error);
      }
    };
    
    /**
     * A new avatar has been created, proceed to create new editing session
     * @param {string} avatar_id 
     */
    const avatarCreated = async (avatar_id) => {
      if (!avatar_id) {
        console.error('Invalid avatar_id:', avatar_id);
        return;
      }
      
      console.log('New avatar created with ID:', avatar_id);
      try {
        await createNewSession(userStore.userId.value, avatar_id, userStore);
      } catch (error) {
        console.error('Error creating session for new avatar:', error);
      }
    };
  
    const handleAvatarImageError = async ({index, id}) => {
      console.warn(`Avatar image error for avatar ${id || index}`);
      
      // Try to regenerate the render URL
      if (userAvatars.value && userAvatars.value[index]) {
        const avatar = userAvatars.value[index];
        
        // Try to fix the render URL
        let fixedUrl = null;
        
        // Option 1: Try a different format of the URL
        if (avatar.render_url && avatar.render_url.includes('final.png')) {
          fixedUrl = avatar.render_url.replace('final.png', 'final.jpg');
        } 
        // Option 2: Generate a completely new URL
        else if (avatar.id) {
          fixedUrl = `${API.AVATURN}/renders/${avatar.id}/final.png`;
        }
        
        if (fixedUrl) {
          console.log(`Trying fixed URL: ${fixedUrl}`);
          userAvatars.value[index] = {...avatar, render_url: fixedUrl};
        }
        
        // You could also trigger a new render request here
        if (avatar.id) {
          try {
            console.log(`Requesting new render for avatar ${avatar.id}`);
            await renderAvatarImage(avatar.id);
            // After a delay, refresh to get the new render
            setTimeout(refreshAvatars, 5000);
          } catch (err) {
            console.error('Error requesting render:', err);
          }
        }
      }
    };

    const handleAvatarImageLoad = ({index, id}) => {
      console.log(`Avatar image loaded successfully for ${id || index}`);
    };

    /**
     * Avaturn viewer has loaded an avatar
     * @param {Object} res 
     */
    const handleAvatarLoaded = (res) => {
      console.log('Avatar loaded from Avaturn:', res);
      avatarLoadedHandler(res, userStore);
    };
    
    /**
     * Handle avatar deletion
     */
    const handleDeleteAvatar = async (avatar) => {
      if (!avatar || !avatar.id) {
        console.error('Invalid avatar to delete:', avatar);
        return;
      }
      
      console.log('Deleting avatar:', avatar);
      try {
        // Use the logger if available, otherwise null
        const loggerObj = logger?.value || null;
        await deleteAvatar(userStore.userId.value, avatar, loggerObj, updateLogger);
        
        // Refresh avatars list after deletion
        await refreshAvatars();
      } catch (error) {
        console.error('Error deleting avatar:', error);
      }
    };
    
    // On component mount
    onMounted(async () => {
      try {
        console.log('App component mounted');
        
        // 1) Fetch product data
        await fetchProductData();
        
        // 2) If no user ID, create new user
        if (!userStore.userId.value) {
          console.log('No user ID, creating new user...');
          const newUser = await createNewUser();
          if (newUser) {
            console.log('New user created:', newUser);
            userStore.userId.value = newUser.id;
            await createLogger(newUser.id);
          }
        } else {
          console.log('User exists, getting data for ID:', userStore.userId.value);
          
          // 3) For existing user: get logs & avatars
          if (getLogger) {
            await getLogger(userStore.userId.value);
          }
          
          // Always fetch avatars directly from Avaturn
          console.log('Fetching avatars from Avaturn...');
          await getUserAvatars(userStore.userId.value);
          console.log('Avatars fetched, count:', userAvatars.value?.length || 0);
          
          // 4) Process avatars if needed
          if (logger?.value?.avatars) {
            console.log('Processing avatars...');
            await processAvatars(userStore.userId.value, logger.value, getLogger, updateLogger);
          }
          
          console.log('Session info:', userStore.sessionId.value, userStore.userAvatarUrl.value,
                      userStore.avatarGender.value, userStore.userBodyId.value);
        }
        
        // 5) Set the initial view mode explicitly
        console.log('Setting view mode to list');
        // setViewMode('list');
        
      } catch (error) {
        console.error('Error during component mount:', error);
        // If error occurs, at least try to show something
        setViewMode('list');
      }
    });
    
    return {
      // Export user store fields
      ...userStore,
      
      // Composable states
      viewMode,
      slug,
      mainProduct,
      products,
      logger,
      isLoading,
      userAvatars,
      
      // Debug state
      showDebug,
      
      // Methods
      setViewMode,
      handleSetViewMode,
      handleAvatarLoaded,
      viewWithBaseModel,
      avatarSelected,
      resetConnection,
      avatarCreated,
      handleDeleteAvatar,
      refreshAvatars,
      forceRerender,
      handleImageError,
      handleAvatarImageError,
      handleAvatarImageLoad
    };
  }
};
</script>

<style>
@import "./assets/css/main.css";

/* Debug panel styling */
.debug-toggle {
  position: fixed;
  top: 10px;
  right: 10px;
  background: rgba(0,0,0,0.7);
  color: white;
  padding: 5px 10px;
  border-radius: 4px;
  cursor: pointer;
  z-index: 9999;
}

.debug-panel {
  position: fixed;
  top: 40px;
  right: 10px;
  background: rgba(0,0,0,0.8);
  color: white;
  padding: 15px;
  border-radius: 4px;
  z-index: 9999;
  width: 300px;
  max-height: 400px;
  overflow-y: auto;
}

.debug-panel button {
  background: #333;
  color: white;
  border: 1px solid #555;
  padding: 5px 10px;
  border-radius: 4px;
  margin-right: 5px;
  margin-top: 5px;
  cursor: pointer;
}
</style>